import { FormEvent, useEffect, useRef, useState } from "react";
import { useChatWidgetText } from "../../../../Helper/ChatWidget";
import Communicator from "../../../../Helper/Communicator";
import { HttpClientXHR } from "../../../../Services/http.clients/config/http.client";
import { validateEmail } from "../../../../Stores/Form";
import { useTicketStore } from "../../../../Stores/Ticket";
import { Button, Card, cx, Foot } from "../../ChatWidgetPrimitives";
import { StepCard } from "./StepCards";

type Phase = "email" | "sending" | "code" | "checking";

const CODE_LENGTH = 6;

/**
 * The "Verify email" workflow step: the address, then the 6-digit code, in one
 * card that changes in place. The server takes the step's exit; a verified
 * email that belongs to another contact hands the widget that contact's
 * session (Communicator.adoptSession).
 */
export const VerifyEmailStepCard = ({ action }: { action: any }) => {
  const text = useChatWidgetText();
  const shareToken = useTicketStore((state) => state.currentTicket?.shareToken);
  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState<string>(action?.knownEmail ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [wrong, setWrong] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeInput = useRef<HTMLInputElement>(null);
  const autoSent = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const post = async (path: string, body: any) => {
    const response = await new HttpClientXHR().post(
      `/v3/shared/tickets/${shareToken}/workflow/verify-email/${path}`,
      body,
      { retry: false } as any,
    );
    return response.data ?? {};
  };

  const sendCode = async (address: string) => {
    if (!shareToken || !validateEmail(address)) return;
    setError(undefined);
    setWrong(false);
    setPhase("sending");
    try {
      const result = await post("send", { email: address });
      if (result.status === "sent") {
        setCode("");
        setResendIn(result.resendIn ?? 30);
        setPhase("code");
        setTimeout(() => codeInput.current?.focus(), 50);
        return;
      }
      if (result.status === "rate_limited" && result.retryIn) {
        // A code went out moments ago: go on with that one.
        setResendIn(result.retryIn);
        setPhase("code");
        return;
      }
      setError(text(result.status === "invalid_email" ? "widgetEmailInvalid" : "widgetVerifyFailed"));
      setPhase("email");
    } catch (_) {
      setError(text("widgetVerifyFailed"));
      setPhase("email");
    }
  };

  // The contact already has an address: send the code there straight away.
  useEffect(() => {
    if (!autoSent.current && action?.knownEmail && shareToken) {
      autoSent.current = true;
      sendCode(action.knownEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  const checkCode = async (value: string) => {
    if (value.length !== CODE_LENGTH || phase === "checking") return;
    setPhase("checking");
    setError(undefined);
    try {
      const result = await post("verify", { code: value });
      if (result.status === "verified") {
        if (result.session) Communicator.adoptSession(result.session, shareToken);
        return; // The step ends; the receipt arrives with the conversation.
      }
      if (result.status === "not_verified") return;
      setPhase("code");
      if (result.status === "wrong_code") {
        setWrong(true);
        setError(
          result.triesLeft === 1
            ? text("widgetWrongCodeOne")
            : text("widgetWrongCode", { n: result.triesLeft }),
        );
      } else if (result.status === "expired" || result.status === "not_sent") {
        setWrong(true);
        setError(text("widgetCodeExpired"));
        setResendIn(0);
      } else {
        setError(text("widgetVerifyFailed"));
      }
    } catch (_) {
      setPhase("code");
      setError(text("widgetVerifyFailed"));
    }
  };

  const onCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    setWrong(false);
    setError(undefined);
    if (digits.length === CODE_LENGTH) checkCode(digits);
  };

  if (phase === "email" || phase === "sending") {
    const canSend = validateEmail(email.trim()) && phase === "email";
    const submit = (event: FormEvent) => {
      event.preventDefault();
      if (canSend) sendCode(email.trim());
    };
    return (
      <StepCard>
        <Card className="cw-form" label={text("widgetVerifyTitle")}>
          <form onSubmit={submit} noValidate>
            <div className="cw-form-head">
              <div className="cw-h">{text("widgetVerifyTitle")}</div>
              <div className="cw-sm cw-muted">{text("widgetVerifyHint")}</div>
            </div>
            <div className="cw-form-group">
              <div className="cw-form-field bot-step-field">
                <input
                  className="cw-form-control"
                  type="email"
                  autoComplete="email"
                  aria-label={text("widgetEmail")}
                  placeholder="you@company.com"
                  value={email}
                  disabled={phase === "sending"}
                  spellCheck={false}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            </div>
            {error && <div className="bot-step-error cw-sm">{error}</div>}
            <Foot>
              <Button type="submit" disabled={!canSend && phase !== "sending"}>
                {phase === "sending" ? (
                  <>
                    <span className="cw-spinner bot-step-spinner" />
                    {text("widgetSendingCode")}
                  </>
                ) : (
                  text("widgetSendCode")
                )}
              </Button>
            </Foot>
          </form>
        </Card>
      </StepCard>
    );
  }

  const minutes = Math.floor(resendIn / 60);
  const seconds = `${resendIn % 60}`.padStart(2, "0");
  return (
    <StepCard>
      <Card className="cw-form" label={text("widgetEnterCode")}>
        <div className="cw-form-head">
          <div className="cw-h">{text("widgetEnterCode")}</div>
          <div className="cw-sm cw-muted">{text("widgetCodeSentTo", { email })}</div>
        </div>
        <label className={cx("bot-step-code", wrong && "bot-step-code--wrong")}>
          <input
            ref={codeInput}
            className="bot-step-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={text("widgetEnterCode")}
            maxLength={CODE_LENGTH}
            value={code}
            disabled={phase === "checking"}
            onChange={(event) => onCodeChange(event.target.value)}
          />
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <span
              key={index}
              aria-hidden="true"
              className={cx(
                "bot-step-digit",
                !wrong && index === code.length && phase !== "checking" && "bot-step-digit--active",
              )}
            >
              {code[index] ?? ""}
            </span>
          ))}
        </label>
        {error && <div className="bot-step-error cw-sm">{error}</div>}
        <Foot>
          <Button variant="ghost" disabled={resendIn > 0 || phase === "checking"} onClick={() => sendCode(email)}>
            {resendIn > 0 ? text("widgetResendIn", { time: `${minutes}:${seconds}` }) : text("widgetResendCode")}
          </Button>
          <Button
            variant="ghost"
            disabled={phase === "checking"}
            onClick={() => {
              setPhase("email");
              setError(undefined);
              setWrong(false);
            }}
          >
            {text("widgetChangeEmail")}
          </Button>
        </Foot>
      </Card>
    </StepCard>
  );
};
